import { z } from 'zod';
import type { SelectedOption } from '../hooks/useAllocationFilters';
import type { ProjectOption } from '../types/Project';

function isValidOption(v: unknown): v is SelectedOption {
  return !!v && typeof (v as SelectedOption).id === 'number';
}

function isValidProject(v: unknown): v is ProjectOption {
  return !!v && typeof (v as ProjectOption).id === 'number';
}

// Mesma invariante do backend (B2 `INVALID_COST_SHARE`): BR + US tem que somar 100.
function checkCostShareSum(
  data: { costShareBr: number; costShareUs: number },
  ctx: z.RefinementCtx,
) {
  if (data.costShareBr + data.costShareUs !== 100) {
    ctx.addIssue({ code: 'custom', path: ['costShareUs'], message: 'costShareSum' });
  }
}

// Mesma invariante do backend (B3 `END_DATE_BEFORE_START_DATE`) — checagem local
// imediata; sobreposição (B4), escopo (B5/B6) e contrato ativo (B7) só o
// servidor sabe responder (`useValidateAllocation`).
function checkEndAfterStart(data: { startDate: Date; endDate: Date | null }, ctx: z.RefinementCtx) {
  if (data.endDate && data.endDate < data.startDate) {
    ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'endBeforeStart' });
  }
}

// Aviso do pool (T3): avisado exige a data do aviso.
function checkNoticeDate(
  data: { notified: boolean; noticeDate: Date | null },
  ctx: z.RefinementCtx,
) {
  if (data.notified && !data.noticeDate) {
    ctx.addIssue({ code: 'custom', path: ['noticeDate'], message: 'required' });
  }
}

/** Mesmo limite do back (`notes`). */
export const NOTES_MAX_LENGTH = 2000;
const currencySchema = z.object({ id: z.number(), iso: z.string() });

export const allocationFormSchema = z
  .object({
    professional: z.custom<SelectedOption>(isValidOption, { message: 'required' }),
    // Só filtra projetos e perfis (legado `onChangeContratoCliente`); não vai no
    // corpo — o back grava o contrato do projeto escolhido.
    contract: z.custom<SelectedOption>(isValidOption, { message: 'required' }),
    project: z.custom<ProjectOption>(isValidProject, { message: 'required' }),
    allocatedPercentage: z.number().int().min(1).max(100),
    billable: z.boolean(),
    startDate: z.date(),
    endDate: z.date().nullable(),
    costShareBr: z.number().int().min(0).max(100),
    costShareUs: z.number().int().min(0).max(100),
    notes: z.string().max(NOTES_MAX_LENGTH).optional(),
    fimConfirmado: z.boolean(),
    tagIds: z.array(z.number()),
    notified: z.boolean(),
    noticeDate: z.date().nullable(),
    noticeDays: z.number().int().min(0).nullable(),
    // Valores (B9 `INVALID_FINANCIAL_VALUE` no back: nenhum negativo). Nulo =
    // campo vazio: custo de alocação anterior aos campos de valor, ou
    // preço/horas apagados (vão como 0).
    salesProfileId: z.number().nullable(),
    monthlyCost: z.number().min(0, 'negative').nullable(),
    hourlyRate: z.number().min(0, 'negative').nullable(),
    soldHours: z.number().min(0, 'negative').nullable(),
    // Só exibição: as moedas vêm do contrato do profissional e do perfil.
    costCurrency: currencySchema.nullable(),
    billingCurrency: currencySchema.nullable(),
  })
  .superRefine(checkCostShareSum)
  .superRefine(checkEndAfterStart)
  .superRefine(checkNoticeDate);

export type AllocationFormValues = z.infer<typeof allocationFormSchema>;
